import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  CircularProgress,
  Container,
  Link as MuiLink,
  Stack,
  Tab,
  Tabs,
  Typography
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import { useNotify } from 'react-admin';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import { RecordSection } from '../components/record/RecordPage.tsx';
import { isDemoSession } from '../apiClient.ts';
import { getErrorMessage } from '../utils/helperUtils.ts';
import {
  doneLine,
  downloadSkippedRows,
  downloadTemplate,
  fetchImportGuide,
  IMPORT_KINDS,
  importKindOf,
  importPath,
  plural,
  runImport,
  type ColumnMapping,
  type ImportGuide,
  type ImportKindInfo,
  type ImportResult,
  type PickedValues
} from './dataImportApi.ts';
import { ImportColumnGuide } from './ImportColumnGuide.tsx';
import { ImportPreview } from './ImportPreview.tsx';

const DataImportPage = () => {
  const { kind: param } = useParams();
  const navigate = useNavigate();
  const kind = importKindOf(param);

  return (
    <Container maxWidth="lg" sx={{ pb: 4 }}>
      <Breadcrumbs sx={{ mt: 2 }}>
        <MuiLink component={RouterLink} underline="hover" color="inherit" to="/">
          Dashboard
        </MuiLink>
        <MuiLink component={RouterLink} underline="hover" color="inherit" to="/settings">
          Settings
        </MuiLink>
        <Typography color="text.primary">Import data</Typography>
      </Breadcrumbs>

      <Box sx={{ mt: 3, mb: 2 }}>
        <Typography
          variant="overline"
          sx={{ color: 'primary.main', fontWeight: 800, letterSpacing: '0.12em' }}
        >
          Settings
        </Typography>
        <Typography variant="h4" sx={{ mt: 0.25, fontWeight: 750, letterSpacing: '-0.025em' }}>
          Import data
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Bring in contacts, companies and opportunities from a spreadsheet. Nothing is saved until
          you have seen what every row will do.
        </Typography>
      </Box>

      <Tabs
        value={kind.kind}
        onChange={(_event, next: string) => navigate(importPath(importKindOf(next).kind))}
        variant="scrollable"
        allowScrollButtonsMobile
        sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
      >
        {IMPORT_KINDS.map((entry) => (
          <Tab key={entry.kind} value={entry.kind} label={entry.label} />
        ))}
      </Tabs>

      {/* A new tab starts over: its own template, file and preview */}
      <ImportPanel key={kind.kind} kind={kind} />
    </Container>
  );
};

const ImportPanel = ({ kind }: { kind: ImportKindInfo }) => {
  const notify = useNotify();
  const demo = isDemoSession();
  const [guide, setGuide] = useState<ImportGuide | null>(null);
  const [guideError, setGuideError] = useState<string | null>(null);
  const [file, setFile] = useState<File | null>(null);
  // Undefined until someone picks a column; the API then detects them itself
  const [mapping, setMapping] = useState<ColumnMapping | undefined>(undefined);
  const [picked, setPicked] = useState<PickedValues>({});
  const [preview, setPreview] = useState<ImportResult | null>(null);
  const [done, setDone] = useState<ImportResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let current = true;
    fetchImportGuide(kind.kind)
      .then((loaded) => current && setGuide(loaded))
      .catch((err) => current && setGuideError(getErrorMessage(err)));
    return () => {
      current = false;
    };
  }, [kind.kind]);

  // Every change of file, column or value previews again
  useEffect(() => {
    if (!file) return;
    let current = true;
    setBusy(true);
    setError(null);
    runImport(kind.kind, file, { dryRun: true, mapping, values: picked })
      .then((result) => {
        if (current) setPreview(result);
      })
      .catch((err) => {
        if (!current) return;
        setPreview(null);
        setError(getErrorMessage(err));
      })
      .finally(() => {
        if (current) setBusy(false);
      });
    return () => {
      current = false;
    };
  }, [kind.kind, file, mapping, picked]);

  const choose = (chosen: File) => {
    setMapping(undefined);
    setPicked({});
    setPreview(null);
    setDone(null);
    setFile(chosen);
  };

  const chooseColumn = (field: string, header: string) => {
    const next: ColumnMapping = { ...(mapping ?? preview?.mapping) };
    if (header) next[field] = header;
    else delete next[field];
    setMapping(next);
  };

  const chooseValue = (field: string, value: string, optionId: string) => {
    const values = { ...picked[field] };
    if (optionId) values[value] = optionId;
    else delete values[value];
    setPicked({ ...picked, [field]: values });
  };

  const template = async () => {
    try {
      await downloadTemplate(kind.kind);
    } catch (err) {
      notify(getErrorMessage(err), { type: 'error' });
    }
  };

  const save = async () => {
    if (!file) return;
    setBusy(true);
    setError(null);
    try {
      const result = await runImport(kind.kind, file, {
        dryRun: false,
        mapping,
        values: picked
      });
      setDone(result);
      setPreview(null);
      setFile(null);
      notify(
        `Imported ${plural(result.summary.create + result.summary.update, kind.one, kind.many)}`,
        {
          type: 'success'
        }
      );
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const writes = preview ? preview.summary.create + preview.summary.update : 0;
  const skippedFile = `${kind.many}-skipped-rows.csv`;

  return (
    <Stack spacing={2}>
      <RecordSection
        title="1. Start from the template"
        description={guide?.rowRule}
        action={
          <Button
            variant="outlined"
            size="small"
            startIcon={<DownloadIcon />}
            onClick={template}
            sx={{ flexShrink: 0 }}
          >
            Template
          </Button>
        }
      >
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          The template&apos;s headers are the ones Forge reads, and its two example rows use your
          own values. Your own spreadsheet works too: Forge matches common column names, and you can
          change any match before importing.
        </Typography>
        {guideError ? (
          <Alert severity="error">Could not load the columns: {guideError}</Alert>
        ) : guide ? (
          <ImportColumnGuide guide={guide} />
        ) : (
          <CircularProgress size={20} />
        )}
      </RecordSection>

      <RecordSection
        title="2. Upload your file"
        description={`A CSV of ${kind.many}, up to 5,000 rows. From Excel, use File, Save As, CSV.`}
      >
        {demo ? (
          <Alert severity="info" sx={{ mb: 1.5 }}>
            The demo shows you the preview but never saves an import, so nothing you upload is shown
            to the next visitor.
          </Alert>
        ) : null}

        {done ? (
          <Alert
            severity="success"
            sx={{ mb: 1.5 }}
            action={
              <Button component={RouterLink} to={kind.listPath} color="inherit" size="small">
                View {kind.many}
              </Button>
            }
          >
            {doneLine(done, kind)}
            {done.summary.error ? (
              <>
                {' '}
                <MuiLink
                  component="button"
                  type="button"
                  color="inherit"
                  onClick={() => downloadSkippedRows(done, skippedFile)}
                  sx={{ verticalAlign: 'baseline' }}
                >
                  Download the skipped rows
                </MuiLink>
              </>
            ) : null}
          </Alert>
        ) : null}

        <Stack
          direction="row"
          spacing={1.5}
          alignItems="center"
          sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}
        >
          <Button
            component="label"
            variant={file ? 'outlined' : 'contained'}
            startIcon={<UploadFileOutlinedIcon />}
            disabled={busy}
          >
            {file ? 'Choose another file' : done ? 'Import another file' : 'Choose CSV file'}
            <input
              hidden
              type="file"
              accept=".csv,text/csv"
              onChange={(event) => {
                const chosen = event.target.files?.[0] ?? null;
                event.target.value = '';
                if (chosen) choose(chosen);
              }}
            />
          </Button>
          {file ? (
            <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>
              {file.name}
            </Typography>
          ) : null}
          {busy ? <CircularProgress size={18} /> : null}
        </Stack>

        {error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : null}

        {preview && guide ? (
          <>
            <ImportPreview
              kind={kind}
              guide={guide}
              preview={preview}
              mapping={mapping ?? preview.mapping}
              busy={busy}
              onColumn={chooseColumn}
              onValue={chooseValue}
            />
            <Stack
              direction="row"
              spacing={1.5}
              alignItems="center"
              sx={{ mt: 2, flexWrap: 'wrap', gap: 1 }}
            >
              <Button
                variant="contained"
                onClick={save}
                disabled={busy || demo || Boolean(preview.missing) || writes === 0}
              >
                {writes ? `Import ${plural(writes, kind.one, kind.many)}` : 'Nothing to import'}
              </Button>
              {preview.summary.error ? (
                <Button onClick={() => downloadSkippedRows(preview, skippedFile)} disabled={busy}>
                  Download the {plural(preview.summary.error, 'skipped row', 'skipped rows')}
                </Button>
              ) : null}
            </Stack>
          </>
        ) : null}
      </RecordSection>
    </Stack>
  );
};

export default DataImportPage;
